import { OwnerType, PointOfView } from '../../lib/graphql';

export type GetShareUrlOptions = {
  platform: SharePlatform;
  pageUrl: string;
  productName: string;
  slug: string;
  search?: URLSearchParams;
};

export type OverrideableGetShareUrlOptions = Pick<GetShareUrlOptions, 'search'>;

export type SharePlatform =
  | 'facebook'
  | 'messenger'
  | 'twitter'
  | 'email'
  | 'whatsapp'
  | 'sms'
  | 'linkedin'
  | 'tiktok'
  | 'youtube'
  | 'slack'
  | 'teams'
  | 'linktree'
  | 'snapchat'
  | 'reddit'
  | 'twitch'
  | 'copy'
  | 'instagram'
  | 'qr';

export const SHARE_PLATFORM_SEARCH_PARAM = 'share-platform';

export function getShareUrl({
  platform,
  pageUrl,
  productName,
  slug,
  search,
}: GetShareUrlOptions): string {
  const url = new URL(pageUrl);

  // TODO refactor when product/marketing decide on unified UTM parameters
  if (platform === 'instagram') {
    url.searchParams.set('utm_source', getUtmSource(platform));
    return url.toString();
  }

  url.searchParams.set('utm_medium', productName);
  url.searchParams.set('utm_content', slug);
  url.searchParams.set('utm_source', getUtmSource(platform));
  url.searchParams.set('utm_campaign', getUtmCampaign(platform));

  if (search) {
    search.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
  }

  return url.toString();
}

function getUtmSource(platform: SharePlatform): string {
  if (platform === 'copy') {
    return 'copyLink';
  }

  if (platform === 'instagram') {
    return 'IG';
  }

  return platform;
}

export function getUtmCampaign(platform: SharePlatform): string {
  if (platform === 'twitter') return 'pfp-tweet';

  if (
    [
      'linktree',
      'reddit',
      'slack',
      'snapchat',
      'teams',
      'tiktok',
      'twitch',
      'youtube',
      'email',
      'whatsapp',
      'sms',
      'linkedin',
    ].includes(platform)
  )
    return `pfp-${platform}`;

  return 'pfp-share';
}

export type ShareMessageOptions = {
  ownerType: OwnerType;
  isPageOwner: boolean;
  allowedPointsOfView: PointOfView[];
};

export type ShareMessageKeySuffix =
  | 'individual.firstPerson'
  | 'individual.thirdPerson'
  | 'organisation.firstPerson'
  | 'organisation.thirdPerson'
  | 'fallback';

export function getShareMessageKeySuffix({
  ownerType,
  isPageOwner,
  allowedPointsOfView,
}: ShareMessageOptions): ShareMessageKeySuffix {
  const pointOfView = selectPointOfView(isPageOwner, allowedPointsOfView);

  switch (ownerType) {
    case 'INDIVIDUAL':
      switch (pointOfView) {
        case 'FIRST_PERSON':
          return 'individual.firstPerson';
        case 'THIRD_PERSON':
          return 'individual.thirdPerson';
      }
    case 'ORGANISATION':
      switch (pointOfView) {
        case 'FIRST_PERSON':
          return 'organisation.firstPerson';
        case 'THIRD_PERSON':
          return 'organisation.thirdPerson';
      }
    default:
      return 'fallback';
  }
}

function selectPointOfView(
  isPageOwner: boolean,
  allowedPointsOfView: PointOfView[],
): PointOfView {
  if (allowedPointsOfView.includes('FIRST_PERSON')) {
    return isPageOwner ? 'FIRST_PERSON' : 'THIRD_PERSON';
  } else {
    return 'THIRD_PERSON';
  }
}
